/**
 * CountdownBlock — Countdown timer content block for hub pages.
 *
 * Two modes:
 * - AUTO: Fetches the nearest future competition from the hub and counts down to it.
 * - FIXED: Counts down to a manually-configured target date.
 *
 * Timer logic extracted from EmbedCountdown with page styling.
 * Uses a ref-tracked interval so it can self-clear when the countdown reaches zero,
 * preventing infinite re-renders after the event goes live.
 */

import {ReactElement, useState, useEffect, useMemo, useRef} from "react";
import {useParams} from "react-router";
import {useGetHubCompetitions} from "@/hooks/hub/useGetHubCompetitions";
import "./CountdownBlock.scss";

interface CountdownBlockProps {
    countdownSource?: "AUTO" | "FIXED";
    countdownTargetDate?: number;
    countdownLabel?: string;
    sectionTitle?: string;
}

interface TimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

export function CountdownBlock({
    countdownSource = "AUTO",
    countdownTargetDate,
    countdownLabel,
    sectionTitle,
}: CountdownBlockProps): ReactElement {
    const {slug} = useParams<{ slug: string }>();

    // Only fetch competitions when AUTO mode — avoids unnecessary API call for FIXED
    const {data, isLoading} = useGetHubCompetitions(
        countdownSource === "AUTO" ? slug : undefined,
        {activeOnly: true, limit: 50},
    );

    // Resolve the target date: AUTO picks nearest future competition, FIXED uses config
    const {targetDate, eventName} = useMemo(() => {
        if (countdownSource === "FIXED" && countdownTargetDate) {
            return {targetDate: countdownTargetDate, eventName: countdownLabel};
        }

        if (!data?.competitions?.length) return {targetDate: null, eventName: null};

        const now = Date.now();
        const future = data.competitions
            .filter((c) => c.startDate && c.startDate > now)
            .sort((a, b) => (a.startDate || 0) - (b.startDate || 0));

        const next = future[0];
        return next
            ? {targetDate: next.startDate!, eventName: next.name}
            : {targetDate: null, eventName: null};
    }, [countdownSource, countdownTargetDate, countdownLabel, data?.competitions]);

    const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
    const [isLive, setIsLive] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!targetDate) {
            setTimeLeft(null);
            setIsLive(false);
            return;
        }

        const update = () => {
            const diff = targetDate - Date.now();
            if (diff <= 0) {
                setTimeLeft(null);
                setIsLive(true);
                // Self-clear: stop ticking once event is live to prevent infinite re-renders
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }
            setIsLive(false);
            setTimeLeft({
                days: Math.floor(diff / 86400000),
                hours: Math.floor(diff % 86400000 / 3600000),
                minutes: Math.floor(diff % 3600000 / 60000),
                seconds: Math.floor(diff % 60000 / 1000),
            });
        };

        update();
        intervalRef.current = setInterval(update, 1000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [targetDate]);

    // AUTO mode still loading — reserve space to prevent layout shift
    if (countdownSource === "AUTO" && isLoading) {
        return <div className="countdown-block countdown-block--loading" />;
    }

    // Nothing to show — no target date resolved and not live
    if (!targetDate && !isLive) return <></>;

    const title = sectionTitle || countdownLabel || eventName;

    return (
        <div className="countdown-block">
            {title &&
                <h3 className="countdown-block__title">{title}</h3>
            }
            {eventName && eventName !== title &&
                <p className="countdown-block__event-name">{eventName}</p>
            }

            {timeLeft
                ? <div className="countdown-block__timer">
                    <CountdownSegment value={timeLeft.days} label="Days" />
                    <span className="countdown-block__separator">:</span>
                    <CountdownSegment value={timeLeft.hours} label="Hrs" />
                    <span className="countdown-block__separator">:</span>
                    <CountdownSegment value={timeLeft.minutes} label="Min" />
                    <span className="countdown-block__separator">:</span>
                    <CountdownSegment value={timeLeft.seconds} label="Sec" />
                </div>
                : isLive &&
                    <p className="countdown-block__live">Event is live!</p>
            }
        </div>
    );
}

function CountdownSegment({value, label}: { value: number; label: string }): ReactElement {
    return (
        <div className="countdown-block__segment">
            <span className="countdown-block__value">
                {String(value).padStart(2, "0")}
            </span>
            <span className="countdown-block__label">{label}</span>
        </div>
    );
}
